// Pure helpers for editing an existing promotion (no React, no network, so they can be unit tested
// with `node --test src/utils/promoEdit.test.mjs`). They turn one row of GET /v3/promotion/listing
// into the shape the Add / Edit Promotion form works with. The server stays the authority on every
// rule: update-offer re-validates everything, this only fills the form in faithfully.
import { encodeTarget, normalizeDays } from "./offerSchedule.js";

const ORDER_TYPES = ["delivery", "takeaway", "table_ordering", "finedining", "roomservice"];
const PAYMENT_MODES = ["1", "2", "3"];

const pad = (n) => String(n).padStart(2, "0");

/** The listing stores these two columns as JSON text, but tolerate an already-decoded array. */
const jsonList = (value) => {
  if (Array.isArray(value)) return value;
  if (typeof value !== "string" || value.trim() === "") return [];
  try {
    const parsed = JSON.parse(value);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

/** 'YYYY-MM-DD' (optionally followed by a time) -> 'YYYY-MM-DD'; anything else -> ''. */
const toIsoDate = (value) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})(?:[ T].*)?$/.exec(String(value || "").trim());
  return m ? `${m[1]}-${m[2]}-${m[3]}` : "";
};

/** '16:05', '16:05:00', '4:05 PM', '04:05pm' -> '16:05'; anything else -> ''. */
const toHm = (value) => {
  const text = String(value || "").trim();
  let m = /^([01]?\d|2[0-3]):([0-5]\d)(?::[0-5]\d)?$/.exec(text);
  if (m) return `${pad(m[1])}:${m[2]}`;
  m = /^(1[0-2]|0?[1-9]):([0-5]\d)\s*([AaPp])\.?[Mm]\.?$/.exec(text);
  if (m) {
    const h = (Number(m[1]) % 12) + (m[3].toLowerCase() === "p" ? 12 : 0);
    return `${pad(h)}:${m[2]}`;
  }
  return "";
};

/** Unix seconds -> { date, time } in the device's local time; invalid -> empty strings. */
const fromUnix = (seconds) => {
  const n = Number(seconds);
  if (!Number.isFinite(n) || n <= 0) return { date: "", time: "" };
  const d = new Date(n * 1000);
  return {
    date: `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`,
    time: `${pad(d.getHours())}:${pad(d.getMinutes())}`,
  };
};

/**
 * Date + time of one end of the validity window. The text columns are what the POS and the admin
 * wrote; the unix timestamp column is the fallback when the text is in some other legacy format.
 */
const windowEnd = (dateText, timeText, unix) => {
  const fallback = fromUnix(unix);
  return {
    date: toIsoDate(dateText) || fallback.date,
    time: toHm(timeText) || fallback.time,
  };
};

const numberText = (value) => (value === null || value === undefined ? "" : String(value));

/**
 * The listing row's current standing: 'expired' | 'scheduled' (not started yet) | 'live'.
 * Uses the unix valid_from / valid_to columns; unknown when either is missing.
 */
export const validityState = (promo, nowSeconds = Math.floor(Date.now() / 1000)) => {
  const from = Number(promo && promo.valid_from);
  const to = Number(promo && promo.valid_to);
  if (!Number.isFinite(to) || to <= 0) return "live";
  if (nowSeconds > to) return "expired";
  if (Number.isFinite(from) && from > nowSeconds) return "scheduled";
  return "live";
};

/**
 * Legacy `ofrdish`: ['all'] (or empty / unreadable) -> null meaning the entire menu; otherwise the
 * list of product ids the promotion is limited to.
 */
const specificDishIds = (promo) => {
  const raw = jsonList(promo && promo.ofrdish);
  if (raw.length === 0 || raw.some((v) => String(v).toLowerCase() === "all")) return null;
  const ids = [];
  raw.forEach((v) => {
    const id = Number(v);
    if (Number.isInteger(id) && id > 0 && !ids.includes(id)) ids.push(id);
  });
  return ids.length ? ids : null;
};

/**
 * Listing row -> the form state of AddPromotionModal (the keys of its emptyForm()).
 *
 * @param {object}  promo            one item of the listing
 * @param {boolean} scheduleEnabled  features.offer_schedule for this restaurant
 * @param {object}  defaults         the modal's emptyForm() - anything the row cannot supply keeps its default
 */
export const promoToForm = (promo, scheduleEnabled, defaults) => {
  const p = promo || {};
  const isCoupon = Number(p.is_coupon) === 1;
  const from = windowEnd(p.validfrm, p.validtimefrm, p.valid_from);
  const to = windowEnd(p.validto, p.validtimeto, p.valid_to);

  const orderTypes = jsonList(p.ofrtypefr).filter((v) => ORDER_TYPES.includes(v));
  const paymentModes = jsonList(p.ofrfr)
    .map(String)
    .filter((v) => PAYMENT_MODES.includes(v));

  const form = {
    ...defaults,
    promo_code: String(p.promocode || ""),
    offer_type: p.ofrtype === "rs" ? "rs" : "per",
    offer_amount: numberText(p.oframnt),
    max_discount_amount: numberText(p.maxdisamt),
    min_amount: numberText(p.minamnt),
    valid_from_date: from.date || defaults.valid_from_date,
    valid_from_time: from.time || defaults.valid_from_time,
    valid_to_date: to.date || defaults.valid_to_date,
    valid_to_time: to.time || defaults.valid_to_time,
    offer_types: orderTypes.length ? orderTypes : defaults.offer_types,
    offer_valid_for: paymentModes.length ? paymentModes : defaults.offer_valid_for,
    one_time_use_only: Number(p.one_time_use_only) === 1,
    offer_available_web: Number(p.offer_available_web) === 1,
    is_coupon: isCoupon,
    details: String(p.details || ""),
    offer_dish_mode: "all",
    offer_dish_ids: [],
  };

  const dishIds = specificDishIds(p);

  if (scheduleEnabled && !isCoupon) {
    // Schedule + Applies-to replace the All / Specific Dishes control for normal promotions.
    const schedule = p.schedule || {};
    if (schedule.type === "days" || schedule.type === "time") {
      form.schedule_type = schedule.type;
      if (schedule.type === "days") form.schedule_days = normalizeDays(schedule.days);
      if (schedule.type === "time") {
        form.schedule_time_from = toHm(schedule.time_from) || defaults.schedule_time_from;
        form.schedule_time_to = toHm(schedule.time_to) || defaults.schedule_time_to;
      }
    }
    let tokens = (Array.isArray(p.targets) ? p.targets : [])
      .map((t) => encodeTarget(t && t.type, t && t.id))
      .filter(Boolean);
    if (tokens.length === 0 && dishIds) {
      // A promotion limited to specific products before targets existed keeps them as product
      // targets. Saving it as "entire menu" instead would silently widen who gets the discount.
      tokens = dishIds.map((id) => encodeTarget("product", id));
    }
    if (tokens.length) {
      form.target_mode = "selected";
      form.target_tokens = tokens;
    }
  } else if (dishIds) {
    form.offer_dish_mode = "specific";
    form.offer_dish_ids = dishIds;
  }

  return form;
};
